import React, {useContext} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import clsx from 'clsx';
import {Skeleton} from 'src/components/skeleton';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useLicenseTypes} from 'src/hooks/dictionaries';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {LocaleContext} from '@pb/kit-context-locale';

import {LicenseSectionSkeleton} from './license-section-skeleton';

import {
    licenseSectionLicenseState,
    licenseSectionLicenseStateActive,
} from './license-section.module.styl';

type Props = {
    accountId: number,
};

export const LicenseSection: React.FC<Props> = ({accountId}) => {
    const {license} = useSelector(selectAccountDetails);
    const navigate = useNavigate();
    const {isFutureDate} = useDateTime();
    const {locale} = useContext(LocaleContext);

    const {getLicenseTypeByCode} = useLicenseTypes();

    const licenseStateClasses = clsx(licenseSectionLicenseState, [
        license?.isActive && licenseSectionLicenseStateActive,
    ]);

    return (
        <Skeleton view={<LicenseSectionSkeleton/>} shown={license === null}>
            <FormSection
                headline="License"
                info={headline => (
                    <Item grow={1}>
                        <Group justify="space-between" alignItems="center">
                            <Group gap="xs" alignItems="center">
                                {headline}
                                <strong className={licenseStateClasses}>
                                    {license?.isActive ? 'Active' : null}
                                    {license && isFutureDate(license?.startDate) ? 'Pending' : null}
                                    {license && !license.isActive && !isFutureDate(license.endDate) ? 'Expired' : null}
                                </strong>
                            </Group>
                            <Button
                                variant="Type.SECONDARY"
                                onClick={() => {
                                    if (license?.licenseId) {
                                        navigate(`/profile/account/${accountId}/license-features/${license?.licenseId}/`);
                                    } else {
                                        navigate(`/profile/account/${accountId}/license-features`);
                                    }
                                }}>
                                Manage
                            </Button>
                        </Group>
                    </Item>
                )}>
                {(license && license.isActive || isFutureDate(license?.endDate)) ? (
                    <Group direction="v" alignItems="start">
                        <Group gap="l" alignItems="center">
                            <strong style={{width: 120}}>License period</strong>
                            <span>
                                {`${formatLicenseDate(
                                    locale.dateFormat,
                                    license?.startDate,
                                )} to ${formatLicenseDate(
                                    locale.dateFormat,
                                    license?.endDate,
                                )}`}
                            </span>
                        </Group>
                        <Group gap="l">
                            <strong style={{width: 120}}>License Type</strong>
                            <span>{license ? getLicenseTypeByCode(license?.typeCode) : ''}</span>
                        </Group>
                    </Group>
                ) : null}
                {(license && !license.isActive && !isFutureDate(license.endDate)) ? (
                    <Group gap="l">
                        <strong style={{width: 120}}>License expired</strong>
                        <span>{formatLicenseDate(locale.dateFormat, license?.endDate)}</span>
                    </Group>
                ) : null}
            </FormSection>
        </Skeleton>
    );
};
